import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  throwError,
  of
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
} from 'rxjs/operators';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { APP_CONFIG_SERVICE } from '../services/common/app-config.service';
import { CurrentUserService } from '../services/common/current-user.service';
import { HttpHeaderService } from '../services/common/http-header.service';
import { LoaderService } from '../services/common/loader.service';
import { MessageService } from '../services/common/message.service';
import { AuthService } from '../services/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { ApiResultStatusCode, MessageType, Routing, messages } from '@medicalapp/shared-core';
import { getDialogConfig } from '@core/helpers/dialog-config.helper';
import { ILoginResponseModel } from '@models/auth/auth.model';
import { CoreService } from '../../services/common/core.service';


let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

// ─────────────────────────────────────────────────────────────────────────────

export const appInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const router = inject(Router);
  const currentUserService = inject(CurrentUserService);
  const authService = inject(AuthService);
  const appConfig = inject(APP_CONFIG_SERVICE);
  const httpHeaderService = inject(HttpHeaderService);
  const loaderService = inject(LoaderService);
  const messageService = inject(MessageService);
  const dialog = inject(MatDialog);
  const coreService = inject(CoreService);
  const displayLoader = coreService.displayLoader();

  // Trigger loader spinner visibility
  if (displayLoader) {
    loaderService.show();
  }

  // Prepend environment base API host if it is a relative API path
  const isUrlRelative = !req.url.startsWith('http://') && !req.url.startsWith('https://');
  const targetUrl = isUrlRelative ? `${appConfig.apiEndpoint}${req.url}` : req.url;

  let mergedHeaders = httpHeaderService.get().set('ngsw-bypass', 'true');
  for (const key of req.headers.keys()) {
    const values = req.headers.getAll(key);
    if (values) {
      mergedHeaders = mergedHeaders.delete(key);
      for (const val of values) {
        mergedHeaders = mergedHeaders.append(key, val);
      }
    }
  }

  const apiReq = req.clone({
    url: targetUrl,
    headers: mergedHeaders
  });

  return next(apiReq).pipe(
    switchMap((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse) {
        const body = event.body;
        if (body && body.statusCode) {
          const statusCode = body.statusCode;
          const message = body.message;

          if (statusCode === ApiResultStatusCode.Forbidden) {
            router.navigate([Routing.Forbidden]);
          } else if (statusCode === ApiResultStatusCode.Unauthorized) {
            // Body-level 401: attempt silent refresh
            return attemptTokenRefresh(apiReq, next, authService, currentUserService);
          } else if (statusCode === ApiResultStatusCode.RequiredReLogin) {
            const dialogConfig = getDialogConfig({
              width: '420px',
              data: {
                heading: messages.NewVersionUpdateHeading,
                message: messages.NewVersionUpdateMessage,
                type: 'warning',
                buttonText: { confirm: 'Logout', cancel: 'Cancel' }
              }
            });
            const dialogRef = dialog.open(ConfirmDialogComponent, dialogConfig);
            dialogRef.afterClosed().subscribe((confirmed: boolean) => {
              if (confirmed) {
                currentUserService.logout();
                dialog.closeAll();
              }
            });
            return of(null as any);
          } else if (statusCode === ApiResultStatusCode.Error) {
            messageService.showMessage(message || messages.DefaultError, MessageType.Error);
            return of(null as any);
          }
        }
      }
      return of(event);
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === ApiResultStatusCode.Forbidden) {
        router.navigate([Routing.Forbidden]);
      } else if (error.status === ApiResultStatusCode.Unauthorized) {
        // HTTP-level 401: attempt silent refresh
        return attemptTokenRefresh(apiReq, next, authService, currentUserService);
      } else if (error.status === ApiResultStatusCode.RequiredReLogin) {
        const dialogConfig = getDialogConfig({
          width: '420px',
          data: {
            heading: messages.NewVersionUpdateHeading,
            message: messages.NewVersionUpdateMessage,
            type: 'warning',
            buttonText: {
              confirm: 'Logout',
              cancel: 'Cancel'
            }
          }
        });
        const dialogRef = dialog.open(ConfirmDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
          if (confirmed) {
            currentUserService.logout();
            dialog.closeAll();
          }
        });
      }

      const serverMessage = error?.error?.message;
      messageService.showMessage(serverMessage || messages.InternetConnectionError, MessageType.Error);
      return throwError(() => error);
    }),
    finalize(() => {
      if (displayLoader) {
        loaderService.hide();
      }
    })
  );
};

/**
 * Handles 401 by attempting a silent token refresh.
 * Uses a BehaviorSubject gate so only ONE refresh call is made even when
 * multiple requests fail simultaneously — others queue behind it.
 */
function attemptTokenRefresh(
  failedRequest: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  currentUserService: CurrentUserService,
): Observable<HttpEvent<any>> {
  if (!authService.hasRefreshToken()) {
    currentUserService.logout();
    return throwError(() => new Error('Session expired'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null); // signal: refresh in-progress

    return authService.refreshToken().pipe(
      switchMap((httpResponse: any) => {
        isRefreshing = false;
        const data: ILoginResponseModel = httpResponse?.body?.data ?? httpResponse?.data;

        if (!data?.jwtToken) {
          // Refresh token was rejected (expired or revoked) — force full re-login
          currentUserService.logout();
          return throwError(() => new Error('Refresh token invalid'));
        }

        // Persist the new tokens silently (only token fields updated)
        currentUserService.updateTokens(
          data.jwtToken,
          data.refreshToken,
          new Date(data.expiration)
        );

        // Signal all waiting requests with the new access token
        refreshTokenSubject.next(data.jwtToken);

        // Retry the original failed request with the new bearer token
        return next(addAuthHeader(failedRequest, data.jwtToken));
      }),
      catchError((err) => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        currentUserService.logout();
        return throwError(() => err);
      })
    );
  }

  // A refresh is already in-flight — queue this request.
  // Once refreshTokenSubject emits the new token, retry with it.
  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((token) => next(addAuthHeader(failedRequest, token!)))
  );
}

/** Clones the request, replacing the Authorization header with the fresh token. */
function addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      Authorization: `bearer ${token}`,
      'ngsw-bypass': 'true',
    },
  });
}
